import { useEffect } from "react";
import { useQueryClient } from "@tanstack/react-query";

// Types
import { IFinanceDetailsSubscriptionResponse } from "../types/model";
import { GraphqlSubscriptionResponse } from "@/Common/api/types/GraphqlSubscriptionResponse";

// Hooks
import { getFniDetailsQueryKey } from "./useFniDetailsQuery";
import { useSubscriptions } from "@/Common/context/subscriptions/Subscriptions";

// Helpers
import { or } from "@/Common/helpers/sonar";

// Subscriptions
import { onUpdateFinanceDetails } from "@DataPages/api/financeManagement/fniDetails/subscriptions";

export function useFniDetailsUpdateSubscription(args: { locationId: string; commissionCycleId: string; payPeriodId: string }) {
	const { locationId, commissionCycleId, payPeriodId } = args;

	const { addSubscription, getSubscription, deleteSubscription } = useSubscriptions();
	const queryClient = useQueryClient();

	const subscriptionKey = ["fniDetailsSub", locationId, commissionCycleId, payPeriodId];

	useEffect(() => {
		if (or(!locationId, !commissionCycleId, !payPeriodId)) {
			return;
		}

		addSubscription(subscriptionKey, [
			{
				context: "onUpdateFinanceDetails",
				query: onUpdateFinanceDetails,
				variables: {
					commissionCycleId,
					payPeriodId,
				},
			},
		]);

		return () => {
			deleteSubscription(subscriptionKey);
		};
	}, [locationId, commissionCycleId, payPeriodId]);

	const financeDetailsSubscription = getSubscription(subscriptionKey);

	financeDetailsSubscription?.subscriptions?.onUpdateFinanceDetails?.setCallback(
		(result: GraphqlSubscriptionResponse<"onUpdateFinanceDetails", IFinanceDetailsSubscriptionResponse>) => {
			// If no elements come in the array it means that there was a batch ingestion
			const hasArrayResponse = Array.isArray(result.data?.onUpdateFinanceDetails?.financeDetails);
			if (!hasArrayResponse) {
				return;
			}

			const queryKey = getFniDetailsQueryKey({ locationId, commissionCycleId, payPeriodId });
			queryClient.resetQueries({ queryKey });
		}
	);
}
